import * as React from "react";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  clearParameters,
  removeParameter,
  setParameters,
} from "../../redux/global/actions";
import { getSerialNumbers } from "../../redux/dynamo_db/actions";
import { useNavigate } from "react-router-dom";
import Button from "@mui/material/Button";
import MultiValueInput from "../../inputs/multiValueInput";
import { Stack } from "@mui/material";
import { downloadCSV, downloadJSON } from "../../utils";
import { DataTable, MaterialSymbol } from "@datalake/towercontrol-banner";
import ExportMenu from "../../widgets/ExportMenu";

const SERIAL_NUMBER_COLUMNS = [
  {
    id: "serial_number",
    label: "Serial Number",
    filter: "text",
    nowrap: true,
  },
  { id: "part_number", label: "Part Number", filter: "text" },
  { id: "shipping_date", label: "Shipping Date", nowrap: true },
  { id: "extract_date", label: "Extract Date", nowrap: true },
  { id: "sold_to", label: "Sold To", filter: "text" },
  { id: "order_id", label: "Order ID", filter: "text" },
  { id: "order_line", label: "Order Line", filter: "text" },
];

export default function SerialNumbers(props) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const queryParameters = useSelector(
    (state) => state?.parameters?.serialNumbers,
  );

  const [currentSerialNumber, setCurrentSerialNumber] = React.useState();
  const [currentBundleReference, setCurrentBundleReference] = React.useState();

  const handleExecuteSearch = () => {
    let queryObject = {};
    if (queryParameters?.serialNumbers) {
      queryObject.serial_number = queryParameters?.serialNumbers;
    }
    if (queryParameters?.bundleReference) {
      queryObject.bundle_reference = queryParameters?.bundleReference;
    }
    dispatch(getSerialNumbers(queryObject));
  };

  function handleSelectMaterial(serialNumber) {
    navigate("/serialnumbers/details/" + serialNumber);
  }

  const handleDeleteSerialNumber = (chipToDelete) => () => {
    dispatch(
      removeParameter({ serialNumbers: { serialNumbers: [chipToDelete] } }),
    );
  };

  const handleClearSerialNumbers = () => {
    dispatch(clearParameters({ serialNumbers: "serialNumbers" }));
  };

  const handleDeleteBundleReference = (chipToDelete) => () => {
    dispatch(
      removeParameter({ serialNumbers: { bundleReference: [chipToDelete] } }),
    );
  };

  const handleClearBundleReference = () => {
    dispatch(clearParameters({ serialNumbers: "bundleReference" }));
  };

  const materials = useSelector((state) => {
    if (state.filteredSerialNumbers) {
      return state.filteredSerialNumbers.map((serialNumber) => {
        return {
          part_number: serialNumber.part_number,
          serial_number: serialNumber.serial_number,
          shipping_date: serialNumber.shipping_date,
          extract_date: serialNumber.extract_date,
          sold_to: serialNumber.sold_to,
          order_id: serialNumber.order_id,
          order_line: serialNumber.order_line,
        };
      });
    } else return [];
  });

  const handleKeydown = (event) => {
    if (event.key == "Enter") {
      event.stopPropagation();
      event.preventDefault();
      setTimeout(() => {
        handleExecuteSearch();
      }, 0);
    }
  };

  useEffect(() => {
    document.addEventListener("keydown", handleKeydown);
    return () => {
      document.removeEventListener("keydown", handleKeydown);
    };
  }, []);

  const handleExportSerialNumbers = (serialNumbers, format = "csv") => {
    const fields = [
      { name: "serial_number" },
      { name: "part_number" },
      { name: "sold_to" },
      { name: "order_id" },
      { name: "order_line" },
      { name: "shipping_date" },
      { name: "extract_date" },
    ];

    if (format === "csv") {
      downloadCSV(fields, "serial_numbers_export", ",", serialNumbers);
    } else {
      downloadJSON(fields, "serial_numbers_export", serialNumbers);
    }
  };

  return (
    <React.Fragment>
      <MultiValueInput
        setCurrentParam={setCurrentSerialNumber}
        setQueryParams={setParameters}
        currentParam={currentSerialNumber}
        queryParams={queryParameters?.serialNumbers}
        handleDelete={handleDeleteSerialNumber}
        handleClear={handleClearSerialNumbers}
        label="Serial Number"
        family="serialNumbers"
        type="serialNumbers"
        disabled={queryParameters?.bundleReference?.length > 0 ? true : false}
      />
      <MultiValueInput
        setCurrentParam={setCurrentBundleReference}
        setQueryParams={setParameters}
        currentParam={currentBundleReference}
        queryParams={queryParameters?.bundleReference}
        handleDelete={handleDeleteBundleReference}
        handleClear={handleClearBundleReference}
        label="Bundle Reference"
        family="serialNumbers"
        type="bundleReference"
        disabled={queryParameters?.serialNumbers?.length > 0 ? true : false}
      />
      <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
        <Button
          variant="contained"
          startIcon={<MaterialSymbol name="search" size="button" />}
          onClick={handleExecuteSearch}
        >
          Search
        </Button>
      </Stack>
      {materials.length > 0 && (
        <DataTable
          title="Serial Numbers"
          subtitle={`${materials.length} results`}
          columns={SERIAL_NUMBER_COLUMNS}
          rows={materials}
          getRowId={(row, i) => `${row?.serial_number}-${i}`}
          onRowClick={(row) => handleSelectMaterial(row?.serial_number)}
          defaultRowsPerPage={25}
          size="small"
          actions={(rows) => (
            <ExportMenu
              disabled={!rows.length}
              onExport={(format) => handleExportSerialNumbers(rows, format)}
            />
          )}
          sx={{ mt: 3 }}
        />
      )}
    </React.Fragment>
  );
}
